import * as React from 'react';
import {
  Dialog as RACDialog,
  DialogTrigger as RACDialogTrigger,
  Modal as RACModal,
  ModalOverlay as RACModalOverlay,
  Heading,
  type DialogProps as RACDialogProps,
  type ModalOverlayProps,
} from 'react-aria-components';
import { cva, type VariantProps } from 'class-variance-authority';
import { CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret Dialog / Modal (handoff §10.8).
 * Primitive: RAC Modal + ModalOverlay + Dialog.
 * --tvl-raised surface, --tvl-r4 (16px), --tvl-e3.
 * Backdrop: --tvl-ink @40% light / @60% dark, no blur.
 * Entrance: fade + 8px rise, d-slow.
 * All compound names preserved for existing consumers.
 */

/* ── Trigger ────────────────────────────────────────────────────────────── */
export const DialogTrigger = RACDialogTrigger;

/* ── Portal / Overlay — thin wrappers kept for naming compat ────────────── */
/** @deprecated Rendered internally by DialogContent — kept for API compat. */
export const DialogPortal: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);

export const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'fixed inset-0 z-[var(--tvl-z-modal)] bg-[var(--tvl-scrim)]',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/* ── Content size variants ───────────────────────────────────────────────── */
const dialogContentVariants = cva(
  [
    'relative w-full outline-none',
    'bg-[var(--tvl-raised)] text-[var(--tvl-ink)]',
    'shadow-[var(--tvl-e3)]',
    'border border-[var(--tvl-hairline)]',
    /* Below sm this is a bottom sheet: flush to the bottom edge, rounded on the
       top corners only, so its actions sit under the thumb instead of floating
       mid-screen. From sm up it is the centred dialog it has always been. */
    'rounded-t-[var(--tvl-r4)] rounded-b-none sm:rounded-[var(--tvl-r4)]',
    /* A dialog MUST be able to scroll inside itself. Centred with no height cap,
       a tall dialog on a phone — or any dialog once the on-screen keyboard takes
       half the viewport — pushed its own footer buttons off-screen with no way
       to reach them. `dvh` tracks the collapsing mobile browser chrome, which
       `vh` does not.
       The box itself is a non-scrolling flex column; the inner wrapper scrolls,
       so the close button stays pinned instead of scrolling out of reach. */
    'flex flex-col max-h-[90dvh] sm:max-h-[85dvh]',
    /* entrance: fade + 8px rise */
    'entering:animate-[dialog-enter_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
    'exiting:animate-[dialog-exit_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'max-w-[400px]',
        md: 'max-w-[560px]',
        lg: 'max-w-[720px]',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

export interface DialogContentProps
  extends Omit<ModalOverlayProps, 'className'>,
    VariantProps<typeof dialogContentVariants> {
  className?: string;
  hideClose?: boolean;
  children?: React.ReactNode;
}

export const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  ({ className, size, hideClose = false, children, ...props }, _ref) => (
    <RACModalOverlay
      className={cn(
        'fixed inset-0 z-[var(--tvl-z-modal)] flex justify-center',
        /* Bottom-anchored and edge-to-edge on a phone (sheet); centred with a
           gutter from sm up (dialog). */
        'items-end p-0 sm:items-center sm:p-4',
        'bg-[var(--tvl-scrim)]',
        'entering:animate-[overlay-enter_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
        'exiting:animate-[overlay-exit_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
      )}
      {...props}
    >
      <RACModal className={cn(dialogContentVariants({ size }), className)}>
        <RACDialog className="flex min-h-0 flex-1 flex-col outline-none">
          {({ close }) => (
            <>
              {/* Grab handle — the affordance that says "this sheet can be
                  dismissed downward". Mobile only; a centred desktop dialog has
                  no drag semantics to advertise. */}
              <div
                aria-hidden
                className="mx-auto mt-3 h-1 w-12 shrink-0 rounded-full bg-[var(--tvl-hairline)] sm:hidden"
              />
              {/* Only this wrapper scrolls, so the close button above it stays
                  reachable no matter how long the content is. */}
              <div
                className={cn(
                  'min-h-0 flex-1 overflow-y-auto',
                  'px-6 pt-6',
                  'pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:pb-6',
                )}
              >
                {children}
              </div>
              {!hideClose && (
                <button
                  onClick={close}
                  aria-label="Close"
                  className={cn(
                    'absolute right-4 top-4 z-10 rounded-[var(--tvl-r1)] p-1',
                    'text-[var(--tvl-ink-3)] transition-colors duration-[var(--tvl-d-fast)]',
                    'hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
                  )}
                >
                  <CloseLine size={20} color="currentColor" aria-hidden />
                </button>
              )}
            </>
          )}
        </RACDialog>
      </RACModal>
    </RACModalOverlay>
  ),
);
DialogContent.displayName = 'DialogContent';

/* ── Root — wraps trigger + content; consumers can also use DialogTrigger ── */
/**
 * Dialog root. Accepts both Radix-style `open`/`onOpenChange` props (legacy)
 * and RAC-style `isOpen`/`onOpenChange` props so existing consumers continue
 * to work without changes.
 */
export interface DialogRootProps extends Omit<React.ComponentProps<typeof RACDialogTrigger>, 'isOpen'> {
  /** Radix compat alias for `isOpen`. */
  open?: boolean;
  isOpen?: boolean;
}

export const Dialog: React.FC<DialogRootProps> = ({ open, isOpen, ...props }) => (
  <RACDialogTrigger isOpen={isOpen ?? open} {...props} />
);

/** Thin shim for consumers that use <DialogClose> as a standalone close button. */
export const DialogClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'rounded-[var(--tvl-r1)] p-1 text-[var(--tvl-ink-3)]',
      'transition-colors duration-[var(--tvl-d-fast)]',
      'hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
      className,
    )}
    aria-label={props['aria-label'] ?? 'Close'}
    {...props}
  >
    {children ?? <CloseLine size={20} color="currentColor" aria-hidden />}
  </button>
));
DialogClose.displayName = 'DialogClose';

/* ── Structural slots ────────────────────────────────────────────────────── */
export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5 text-left', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        // flex-wrap: when two long-labelled buttons can't fit side-by-side in a narrow
        // (size="sm") dialog, they wrap to separate lines instead of overflowing and
        // spilling their text past the dialog edge.
        'mt-4 flex flex-col-reverse gap-2 border-t border-[var(--tvl-hairline)] pt-4 sm:flex-row sm:flex-wrap sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

export const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <Heading
    ref={ref}
    slot="title"
    className={cn('text-lg font-semibold leading-none text-[var(--tvl-ink)]', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-[var(--tvl-ink-2)]', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';
